import Vue from 'vue'
import Router from 'vue-router'

import AuthService from '@/core/auth/AuthService'
import { getHomeRouteForLoggedInUser, getUserData } from '@/core/auth/utils'
import { isMetrikaEmbedding } from '@/helpers/isMetrikaEmbedding'
import apps from '@/router/app' // Предполагается, что здесь лежат ваши основные маршруты
import store from '@/store'
import { addToHistory } from '@/utils/history'

Vue.use(Router)

const router = new Router({
    mode: 'history',
    base: import.meta.env.BASE_URL,
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes: [
        {
            path: '/',
            redirect: getHomeRouteForLoggedInUser,
        },
        ...apps,
        {
            path: '/error-access',
            name: 'error-access',
            component: () =>
                import('@/views/pages/miscellaneous/NotEnoughPermissions.vue'),
            meta: {
                layout: 'full',
                allowMobile: true,
            },
        },
        {
            path: '/error-404',
            name: 'error-404',
            component: () => import('@/views/error/Error404.vue'),
            meta: {
                layout: 'full',
                allowMobile: true,
            },
        },
        {
            path: '/not-authorized',
            name: 'misc-not-authorized',
            component: () =>
                import('@/views/pages/miscellaneous/NotAuthorized.vue'),
            meta: {
                layout: 'full',
                resource: 'Auth',
                allowMobile: true,
            },
        },
        {
            path: '*',
            redirect: { name: 'error-404' },
        },
    ],
})

router.beforeEach((to, from, next) => {
    // сохраняем текущий роут в сторе (как у вас было)
    store.commit('app/SET_ROUTE', to)

    // определяем режим Метрики
    const inMetrika = Boolean(window.__READ_ONLY__) || isMetrikaEmbedding()
    if (inMetrika) {
        // фиксируем флаг, чтобы им можно было пользоваться в компонентах/UI
        window.__READ_ONLY__ = true
    }

    // что считаем «защищённой» страницей
    const isProtectedRoute = to.meta.layout !== 'full'

    // статус аутентификации
    const hasKeycloakInit = AuthService.isInitialized === true
    const hasAuthToken =
        typeof AuthService.isAuthenticated === 'function'
            ? AuthService.isAuthenticated()
            : false
    const isAuthenticated = hasKeycloakInit && hasAuthToken

    // данные пользователя (могут отсутствовать в read-only)
    const userData = (() => {
        try {
            return JSON.parse(localStorage.getItem('userData') || 'null')
        } catch {
            return null
        }
    })()

    // 1) Защита маршрутов.
    //    Если маршрут защищённый и пользователя ещё нет:
    if (isProtectedRoute && !isAuthenticated) {
        if (inMetrika) {
            // Во фрейме Метрики НЕ блокируем: позволяем рендериться без токена.
            return next()
        }
        // В обычном режиме ведём себя как раньше: не даём перейти.
        // (Ваш main.js форсит логин, так что здесь просто прерываем навигацию,
        //  чтобы не было артефактов в UI до логина.)
        console.warn(
            'Blocked navigation to a protected route: user not authenticated.'
        )
        return
    }

    // 2) ACL (права доступа).
    //    В обычном режиме — проверяем.
    //    В режиме Метрики — пропускаем, чтобы страница не падала без userData.
    if (!inMetrika && userData && to.meta.permission) {
        const userPermissions = userData.permissions || []
        const requiredPermissions = Array.isArray(to.meta.permission)
            ? to.meta.permission
            : [to.meta.permission]

        const hasPermission = requiredPermissions.some(p =>
            userPermissions.includes(p)
        )

        if (!hasPermission) {
            if (from.name === 'error-access') {
                return next(false) // abort, остаёмся на странице без перехода
            }

            return next({ name: 'error-access' })
        }
    }

    // 3) Ограничения для мобильных.
    //    В режиме Метрики пропускаем (иначе можем уехать на другие экраны).
    const isMobile = store.getters['app/isMobile']
    if (!inMetrika && isMobile && !to.meta.allowMobile) {
        const mobileRedirects = {
            'order.list': 'order-list',
            'foreman.list': 'foreman-ticket-time',
            'order-department.list': 'ticket-time',
        }
        const redirectRouteName = mobileRedirects[to.meta.permission]
        return next(
            redirectRouteName
                ? { name: redirectRouteName }
                : { name: 'error-access' }
        )
    }

    // 4) Всё ок — идём дальше.
    next()
})

router.afterEach(to => {
    const appLoading = document.getElementById('loading-bg')

    if (appLoading) {
        appLoading.style.display = 'none'
    }

    const { user } = getUserData()

    if (user) {
        setTimeout(() => {
            // TODO 96162034 вынести в envs
            if (window.ym) {
                ym(96162034, 'hit', to.fullPath)
                ym(96162034, 'userParams', {
                    user_id: user.id,
                    login: user.login,
                })
            }
        }, 1000)
    }

    addToHistory(to.fullPath)
})

export default router
